// Pure helpers for the Funnels panel: the step editor's draft model, a mirror
// of the server's step validation (studio funnels/domain/steps.ts) so invalid
// funnels are caught before Save, and stats formatting. No React/store imports.
import { FUNNEL_STEP_TYPES } from '@dunea/shared';
import type { FunnelStatus, FunnelStep, FunnelStepInput, FunnelStepStats } from '~/lib/api/funnels';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export {
  apiErrorMessage,
  formatMinor,
  majorToMinor,
  minorToMajorInput,
  recurringLabel,
  slugify,
} from '~/components/workbench/tools/payments/payments-model';

export const STEP_TYPES: readonly string[] = FUNNEL_STEP_TYPES;

export const OFFER_STEP_TYPES: ReadonlySet<string> = new Set(['checkout', 'order-bump', 'upsell', 'downsell']);

export const BRANCHING_STEP_TYPES: ReadonlySet<string> = new Set(['upsell', 'downsell']);

const STEP_LABELS: Record<string, string> = {
  landing: 'Landing',
  optin: 'Opt-in',
  checkout: 'Checkout',
  'order-bump': 'Order bump',
  upsell: 'Upsell',
  downsell: 'Downsell',
  'thank-you': 'Thank you',
};

export function stepTypeLabel(type: string): string {
  return STEP_LABELS[type] ?? type;
}

/** An editable step. `key` is the existing step id, or a fresh `new-…` key. */
export interface StepDraft {
  key: string;
  type: string;
  path: string;
  offerId: string | null;
  onAccept: string | null;
  onDecline: string | null;
  config: Record<string, unknown> | null;
}

export function draftsFromSteps(steps: FunnelStep[]): StepDraft[] {
  return [...steps]
    .sort((a, b) => a.position - b.position)
    .map((s) => ({
      key: s.id,
      type: s.type,
      path: s.path,
      offerId: s.offerId,
      onAccept: s.onAccept,
      onDecline: s.onDecline,
      config: s.config,
    }));
}

/**
 * Drafts → the PUT steps body. Fields that don't apply to a step's type are
 * dropped (an offer on a landing step, a pointer on a checkout) so changing a
 * step's type never leaves a stale value the server would reject.
 */
export function draftsToInputs(drafts: StepDraft[]): FunnelStepInput[] {
  return drafts.map((d) => ({
    key: d.key,
    type: d.type,
    path: d.path.trim(),
    offerId: OFFER_STEP_TYPES.has(d.type) ? d.offerId : null,
    onAccept: BRANCHING_STEP_TYPES.has(d.type) ? d.onAccept : null,
    onDecline: BRANCHING_STEP_TYPES.has(d.type) ? d.onDecline : null,
    ...(d.config ? { config: d.config } : {}),
  }));
}

/** A path for a new step that isn't taken yet: "/upsell", "/upsell-2"… */
export function suggestPath(type: string, taken: readonly string[]): string {
  const stem = `/${type === 'thank-you' ? 'thank-you' : type}`;
  const used = new Set(taken);

  if (!used.has(stem)) {
    return stem;
  }

  for (let i = 2; ; i++) {
    if (!used.has(`${stem}-${i}`)) {
      return `${stem}-${i}`;
    }
  }
}

let keySeq = 0;

export function newStepDraft(type: string, existing: readonly StepDraft[]): StepDraft {
  keySeq += 1;

  return {
    key: `new-${Date.now().toString(36)}-${keySeq}`,
    type,
    path: suggestPath(
      type,
      existing.map((d) => d.path),
    ),
    offerId: null,
    onAccept: null,
    onDecline: null,
    config: null,
  };
}

export function moveStep(drafts: StepDraft[], index: number, by: -1 | 1): StepDraft[] {
  const to = index + by;

  if (to < 0 || to >= drafts.length) {
    return drafts;
  }

  const next = [...drafts];
  [next[index], next[to]] = [next[to], next[index]];

  return next;
}

/** Remove a step and clear any upsell/downsell pointers that targeted it. */
export function removeStep(drafts: StepDraft[], index: number): StepDraft[] {
  const gone = drafts[index]?.key;

  return drafts
    .filter((_, i) => i !== index)
    .map((d) => ({
      ...d,
      onAccept: d.onAccept === gone ? null : d.onAccept,
      onDecline: d.onDecline === gone ? null : d.onDecline,
    }));
}

/** Where an upsell/downsell at `index` may point: later, non-bump steps. */
export function branchTargets(drafts: StepDraft[], index: number): StepDraft[] {
  return drafts.filter((d, j) => j > index && d.type !== 'order-bump');
}

/**
 * Every structural problem, in order — the server's `stepProblems` with
 * owner-facing wording. Empty array = Save will be accepted.
 */
export function stepProblems(drafts: readonly StepDraft[], offerIds: ReadonlySet<string>): string[] {
  const problems: string[] = [];
  const label = (i: number) => `Step ${i + 1} (${stepTypeLabel(drafts[i].type)})`;

  if (!drafts.length) {
    return ['A funnel needs at least one step.'];
  }

  const paths = new Set<string>();
  const indexByKey = new Map(drafts.map((s, i) => [s.key, i]));

  drafts.forEach((step, i) => {
    const path = step.path.trim();

    if (!STEP_TYPES.includes(step.type)) {
      problems.push(`${label(i)}: unknown step type.`);
    }

    if (!/^\/[A-Za-z0-9/_\-.~]*$/.test(path)) {
      problems.push(`${label(i)}: the path must start with "/" and use only letters, numbers, - _ . ~ /.`);
    } else if (paths.has(path)) {
      problems.push(`${label(i)}: the path ${path} is used twice.`);
    }

    paths.add(path);

    if (OFFER_STEP_TYPES.has(step.type)) {
      if (!step.offerId) {
        problems.push(`${label(i)}: pick an offer.`);
      } else if (!offerIds.has(step.offerId)) {
        problems.push(`${label(i)}: its offer was deleted; pick another.`);
      }
    }

    if (!BRANCHING_STEP_TYPES.has(step.type)) {
      return;
    }

    for (const [field, target] of [
      ['If accepted', step.onAccept],
      ['If declined', step.onDecline],
    ] as const) {
      if (!target) {
        continue;
      }

      const j = indexByKey.get(target);

      if (j === undefined) {
        problems.push(`${label(i)}: "${field}" points to a removed step.`);
      } else if (drafts[j].type === 'order-bump') {
        problems.push(`${label(i)}: "${field}" can't point to an order bump.`);
      } else if (j <= i) {
        problems.push(`${label(i)}: "${field}" must point to a later step.`);
      }
    }
  });

  const checkouts = drafts.map((s, i) => (s.type === 'checkout' ? i : -1)).filter((i) => i >= 0);

  if (checkouts.length !== 1) {
    problems.push(`A funnel needs exactly one checkout step (it has ${checkouts.length}).`);
  }

  const checkout = checkouts[0] ?? -1;

  if (checkout >= 0) {
    drafts.forEach((step, i) => {
      if (BRANCHING_STEP_TYPES.has(step.type) && i < checkout) {
        problems.push(`${label(i)}: must come after the checkout step.`);
      }
    });

    let end = checkout + 1;

    while (end < drafts.length && drafts[end].type === 'order-bump') {
      end++;
    }

    drafts.forEach((step, i) => {
      if (step.type === 'order-bump' && (i <= checkout || i >= end)) {
        problems.push(`${label(i)}: order bumps must sit directly after the checkout step.`);
      }
    });
  }

  if (drafts[drafts.length - 1].type !== 'thank-you') {
    problems.push('The last step must be a thank-you step.');
  }

  return problems;
}

/** A starter funnel: landing → checkout → thank-you (offer picked by the owner). */
export function starterDrafts(): StepDraft[] {
  const drafts: StepDraft[] = [];

  for (const type of ['landing', 'checkout', 'thank-you']) {
    drafts.push(newStepDraft(type, drafts));
  }

  return drafts;
}

export function draftsEqual(a: StepDraft[], b: StepDraft[]): boolean {
  return JSON.stringify(draftsToInputs(a)) === JSON.stringify(draftsToInputs(b));
}

// ─── status ───

export function funnelStatusMeta(status: string): { label: string; tone: Tone } {
  switch (status as FunnelStatus) {
    case 'active':
      return { label: 'Active', tone: 'success' };
    case 'archived':
      return { label: 'Archived', tone: 'muted' };
    default:
      return { label: 'Draft', tone: 'muted' };
  }
}

// ─── stats ───

export function percentLabel(ratio: number): string {
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return '0%';
  }

  const pct = ratio * 100;

  return `${pct >= 10 ? Math.round(pct) : Number(pct.toFixed(1))}%`;
}

/** Bump take rates for a checkout row: takes / paid checkouts. */
export function bumpRates(
  row: Pick<FunnelStepStats, 'bumpTakes' | 'purchases'>,
): { offerId: string; takes: number; rate: number }[] {
  return Object.entries(row.bumpTakes ?? {}).map(([offerId, takes]) => ({
    offerId,
    takes,
    rate: row.purchases ? takes / row.purchases : 0,
  }));
}

/** What a step's conversion means, for the stats table caption. */
export function conversionMeaning(type: string): string {
  return OFFER_STEP_TYPES.has(type) ? 'bought' : 'went on';
}
